import { useTranslations } from 'next-intl'
import { useMemo, useState } from 'react'

import { Button } from '@/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { useCompanyAgents, useConversationFeedback, useMessageVersions, useRegenerateMessage } from '@/hooks/useApi'
import { useNavigateToBranch } from '@/hooks/useChatActions'
import { useFeatureFlags } from '@/hooks/useFeatureFlags'
import { useAiModelStore } from '@/store/useAiModelStore'
import { useChatStore } from '@/store/useChatStore'
import { ChevronDown, ChevronLeft, ChevronRight, Copy, RefreshCcw, ThumbsDown, ThumbsUp, Volume2 } from 'lucide-react'
import { toast } from 'sonner'

interface ChatMessageToolbarProps {
  content: string
  conversationId: number
  feedback: boolean | null
  versionNo: number
  totalVersions: number
  versionRootId: number | null
  modelVersionName: string | null
  agentId: number | null
  agentName: string | null
  isOwner?: boolean
  isBroadcast?: boolean
}

export function ChatMessageToolbar({
  content,
  conversationId,
  feedback,
  versionNo,
  totalVersions,
  versionRootId,
  modelVersionName,
  agentId,
  agentName,
  isOwner = true,
  isBroadcast = false,
}: ChatMessageToolbarProps) {
  const flags = useFeatureFlags()
  const hasSessionRoom = useChatStore((state) => state.session?.room != null)
  const enabledModels = useAiModelStore((state) => state.enabledModels)
  const selectedModel = useAiModelStore((state) => state.selectedRegenerateModelId)
  const setSelectedModel = useAiModelStore((state) => state.setSelectedRegenerateModel)
  const [selectedTarget, setSelectedTarget] = useState(agentId ? `agent:${agentId}` : `model:${selectedModel}`)
  const companyAgentsQuery = useCompanyAgents(flags.enableAgentSelection)
  const modelOptions = useMemo(
    () =>
      enabledModels.map((model) => ({
        value: `model:${model.version_id}`,
        label: `${model.provider_display_name || model.provider_label} - ${model.version_display_name || model.label}`,
      })),
    [enabledModels],
  )
  const agentOptions = useMemo(
    () =>
      (companyAgentsQuery.data ?? []).map((agent) => ({
        value: `agent:${agent.id}`,
        label: agent.name,
      })),
    [companyAgentsQuery.data],
  )
  const targetOptions = useMemo(
    () => [...(flags.enableModelSelection ? modelOptions : []), ...(flags.enableAgentSelection ? agentOptions : [])],
    [agentOptions, flags.enableAgentSelection, flags.enableModelSelection, modelOptions],
  )

  const selectedTargetLabel = targetOptions.find((target) => target.value === selectedTarget)?.label ?? 'Select model'
  const displayLabel = agentName ?? modelVersionName ?? selectedTargetLabel

  const t = useTranslations('chat')
  const regenerateChatMessage = useChatStore((state) => state.regenerateMessage)
  const setConversationFeedback = useChatStore((state) => state.setConversationFeedback)
  const feedbackMutation = useConversationFeedback()
  const { mutateAsync: regenerateMessageFn, isPending: isRegenerating, invalidate } = useRegenerateMessage()
  const navigateToBranch = useNavigateToBranch()
  const rootConvId = versionRootId ?? conversationId

  const handleRegenerate = async (target: string) => {
    const [targetType, targetId] = target.split(':')
    await regenerateChatMessage({
      conversationId,
      modelVersionId: targetType === 'model' ? Number(targetId) : null,
      agentId: targetType === 'agent' ? Number(targetId) : null,
      regenerateMessage: regenerateMessageFn,
      onComplete: invalidate,
    })
  }
  const { data: versions } = useMessageVersions(totalVersions > 1 ? rootConvId : undefined)

  const currentIndex = versions?.findIndex((v) => v.version_no === versionNo) ?? -1
  const canGoPrev = currentIndex > 0
  const canGoNext = currentIndex < (versions?.length ?? 0) - 1

  const goToPrev = () => {
    if (!canGoPrev || !versions) return
    navigateToBranch(versions[currentIndex - 1].branch_id)
  }

  const goToNext = () => {
    if (!canGoNext || !versions) return
    navigateToBranch(versions[currentIndex + 1].branch_id)
  }

  const handleFeedback = async (value: boolean) => {
    const newFeedback = feedback === value ? null : value
    setConversationFeedback(conversationId, newFeedback)
    try {
      await feedbackMutation.mutateAsync({ pk: conversationId, feedback: newFeedback })
    } catch {
      setConversationFeedback(conversationId, feedback)
      toast.error(t('feedbackError'), { description: t('feedbackErrorDescription') })
    }
  }

  const copyMessage = async () => {
    try {
      await navigator.clipboard.writeText(content)
      toast.success(t('copiedMessage'), { description: t('copiedMessageDescription') })
    } catch {
      toast.error(t('copyError'), { description: t('copyErrorDescription') })
    }
  }

  return (
    <div className="flex w-fit items-center gap-2 border border-main-border-lv03 rounded-300 bg-main-surface-lv-02 p-2 text-main-text-lv02 shadow-elevation-lv03">
      {totalVersions > 1 && (
        <div className="flex items-center rounded-200 bg-main-surface-lv-02-dim">
          <Button
            type="button"
            variant="ghost"
            size="icon-md"
            className="h-8 w-8 rounded-200 px-2 py-1 text-main-text-lv02 hover:bg-transparent"
            onClick={goToPrev}
            disabled={!canGoPrev}
          >
            <ChevronLeft className="size-4" />
          </Button>
          <span className="text-ui-body-b2 font-medium leading-ui-body-b2 text-main-text-lv02">
            {versionNo} / {totalVersions}
          </span>
          <Button
            type="button"
            variant="ghost"
            size="icon-md"
            className="h-8 w-8 rounded-200 px-2 py-1 text-main-text-lv02 hover:bg-transparent"
            onClick={goToNext}
            disabled={!canGoNext}
          >
            <ChevronRight className="size-4" />
          </Button>
        </div>
      )}

      <div className="flex items-center gap-1.5">
        <Button type="button" variant="ghost" size="icon-sm">
          <Volume2 className="size-4" />
        </Button>
        <Button type="button" variant="ghost" size="icon-sm" onClick={copyMessage}>
          <Copy className="size-4" />
        </Button>
        {isOwner && (
          <>
            <Button type="button" variant="ghost" size="icon-sm" onClick={() => handleFeedback(true)}>
              <ThumbsUp className="size-4" />
            </Button>
            <Button type="button" variant="ghost" size="icon-sm" onClick={() => handleFeedback(false)}>
              <ThumbsDown className="size-4" />
            </Button>
          </>
        )}
      </div>

      {isOwner && !isBroadcast && !hasSessionRoom && (
        <DropdownMenu>
          <DropdownMenuTrigger
            className="flex items-center rounded-200 bg-main-surface-lv-02-dim text-main-text-lv02 outline-none transition-colors hover:bg-main-surface-lv-02-dim"
            disabled={isRegenerating || targetOptions.length === 0}
          >
            <div className="flex h-8 w-8 items-center justify-center rounded-200 px-2 py-1">
              <RefreshCcw className={`size-4 ${isRegenerating ? 'animate-spin' : ''}`} />
            </div>
            <span className="text-ui-body-b2 font-medium leading-ui-body-b2 text-main-text-lv02">{displayLabel}</span>
            <div className="flex h-8 w-8 items-center justify-center rounded-200 px-2 py-1">
              <ChevronDown className="size-4" />
            </div>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="min-w-36">
            <DropdownMenuRadioGroup
              value={selectedTarget}
              onValueChange={(value) => {
                setSelectedTarget(value)
                if (value.startsWith('model:')) setSelectedModel(value.slice('model:'.length))
                handleRegenerate(value)
              }}
              aria-label="Select model or agent"
            >
              {targetOptions.map((target) => (
                <DropdownMenuRadioItem key={target.value} value={target.value}>
                  {target.label}
                </DropdownMenuRadioItem>
              ))}
            </DropdownMenuRadioGroup>
          </DropdownMenuContent>
        </DropdownMenu>
      )}
    </div>
  )
}
